import { Table as BaseTable, Box, Button, Center, Collapse, LoadingOverlay, Pagination, Select, Space, Text } from "@mantine/core";
import { IconChevronDown, IconChevronUp, IconDatabaseOff, IconMinus, IconPlus, IconSelector } from "@tabler/icons-react";
import qs from "qs";
import React, { useImperativeHandle } from "react";
import useSwr from "swr";
import { IFilter } from "../../interfaces/IFilter";
import classes from "./styles.module.css";

export * from "./RowAction";

export type ColumnType<T> = {
  title: string;
  sorter?: boolean;
  dataIndex?: string;
  render: (record: T, index: number) => React.JSX.Element | string;
  align?: "left" | "right" | "center";
  width?: string | number;
};

type Props = {
  name: string;
  columns: ColumnType<any>[];
  filters?: { [key: string]: string | number };
  pagination?: boolean;
  dataSource?: any[];
  loadData?: (filter?: IFilter) => Promise<any>;
  collapse?: boolean;
  collapseChildren?: any;
  onResult?: any;
};

export type ITableRef = { reload: () => void };

export const Table = React.forwardRef(
  (
    { name, columns, filters = {}, pagination = true, dataSource = [], loadData, collapse = false, collapseChildren, onResult }: Props,
    ref: React.Ref<ITableRef>,
  ) => {
    const [opened, setOpened] = React.useState<number[]>([]);
    const scrollRef = React.useRef<HTMLDivElement>(null);
    const tableRef = React.useRef<HTMLTableElement>(null);
    const [offset, setOffset] = React.useState({ page: 1, limit: 20 });

    const { data, mutate, isLoading } = useSwr(
      `table.${name}.[${offset.page}, ${offset.limit}]?${qs.stringify(filters)}`,
      async () => loadData && (await loadData({ offset, filter: filters } as IFilter)),
      {
        fallbackData: { count: dataSource.length, rows: dataSource },
      },
    );
    const [sorted, setSorting] = React.useState<string[]>([]);

    const onSort = (index?: string) => {
      if (index) setSorting((state) => [index, state[0] === index ? (state[1] === "asc" ? "desc" : "asc") : "asc"]);
    };

    const onScroll = () => {};

    useImperativeHandle(ref, () => ({
      reload() {
        return mutate(undefined, true);
      },
    }));

    return (
      <Box className={classes.container}>
        <Box ref={scrollRef} onScroll={onScroll} className={classes.scroll}>
          <BaseTable ref={tableRef} horizontalSpacing="md" verticalSpacing="xs" striped>
            <BaseTable.Thead>
              <BaseTable.Tr>
                {collapse && <BaseTable.Th className={classes.collapseTh}></BaseTable.Th>}
                {columns.map((column, index) => (
                  <Th key={index} column={column} sorted={sorted[0] === column.dataIndex && sorted[1]} onSort={() => onSort(column.dataIndex)}>
                    {column.title}
                  </Th>
                ))}
              </BaseTable.Tr>
            </BaseTable.Thead>
            {data?.rows?.length > 0 && onResult && onResult(data)}
            <BaseTable.Tbody>
              {data?.rows?.length > 0 ? (
                data?.rows?.map((row: any, index: number) => (
                  <React.Fragment key={index}>
                    <BaseTable.Tr>
                      {collapse && (
                        <BaseTable.Td>
                          <Button
                            onClick={() => setOpened(opened.includes(index) ? opened.filter((f) => f !== index) : [...opened, index])}
                            size="xs"
                            w={25}
                            h={25}
                            p={0}
                            variant="default">
                            {opened.includes(index) ? <IconMinus size={18} stroke={1} /> : <IconPlus size={18} stroke={1} />}
                          </Button>
                        </BaseTable.Td>
                      )}
                      {columns.map((column, index2) => (
                        <BaseTable.Td key={index2} align={column.align || "left"}>
                          {column.render(row, index)}
                        </BaseTable.Td>
                      ))}
                    </BaseTable.Tr>
                    {collapse && (
                      <BaseTable.Tr key={`${index}_collapse`}>
                        <BaseTable.Td colSpan={columns.length + 1}>
                          <Collapse expanded={opened.includes(index)}>
                            <Box className={classes.collapseContent}>{collapse && collapseChildren && collapseChildren(row)}</Box>
                          </Collapse>
                        </BaseTable.Td>
                      </BaseTable.Tr>
                    )}
                  </React.Fragment>
                ))
              ) : (
                <BaseTable.Tr key="nothing">
                  <BaseTable.Td colSpan={columns.length}>
                    <Center className={classes.emptyCenter}>
                      <IconDatabaseOff />
                      <Text fw={500} ta="center">
                        Өгөгдөл олдсонгүй
                      </Text>
                    </Center>
                  </BaseTable.Td>
                </BaseTable.Tr>
              )}
            </BaseTable.Tbody>
          </BaseTable>
          <Space h="md" />
          <LoadingOverlay visible={isLoading} />
        </Box>

        {pagination && (
          <Box className={classes.pagination}>
            <Pagination
              total={Math.ceil(data.count / offset.limit)}
              value={offset.page}
              onChange={(page) => setOffset((state) => ({ ...state, page: page }))}
              siblings={1}
              boundaries={1}
              withControls={true}
              disabled={Math.ceil(data.count / offset.limit) <= 1}
              hideWithOnePage
            />
            {data.count > 0 && data && (
              <Select
                onChange={(e: string | null) => {
                  if (e !== null) {
                    setOffset((state) => ({ ...state, limit: parseInt(e, 10), page: 1 }));
                  }
                }}
                value={`${offset.limit}`}
                w={"100px"}
                size="xs"
                defaultValue={"10"}
                data={["5", "10", "20", "30", "50", "100", "200"]}
              />
            )}
          </Box>
        )}
      </Box>
    );
  },
);

interface ThProps {
  children: React.ReactNode;
  column: ColumnType<any>;
  sorted?: string | unknown;
  onSort(): void;
}

const Th = ({ children, column, sorted, onSort }: ThProps) => {
  const Icon = sorted ? (sorted === "asc" ? IconChevronUp : IconChevronDown) : IconSelector;

  if (!column.sorter) return <BaseTable.Th style={{ width: column.width }}>{children}</BaseTable.Th>;

  return (
    <BaseTable.Th style={{ width: column.width }} onClick={() => onSort()}>
      <Box className={classes.button}>
        <Box component="span" className={classes.child}>
          {children}
        </Box>
        <Box component="span" className={classes.icon}>
          <Icon size={14} stroke={1.5} />
        </Box>
      </Box>
    </BaseTable.Th>
  );
};
